import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CodeMirror, { EditorView, ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { autocompletion } from "@codemirror/autocomplete";
import { redo, undo } from "@codemirror/commands";
import { javascript } from "@codemirror/lang-javascript";
import { Diagnostic, linter, lintGutter } from "@codemirror/lint";
import { Text } from "@codemirror/state";

import { FormulaSchema, FormulaValidationResult } from "../../model/formula";

import { createFormulaCompletionSource, findStructuralIssues } from "./formulaCompletion";
import { FormulaFieldPalette } from "./FormulaFieldPalette";

import "./formulaEditor.css";

export interface FormulaEditorProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Root-object schema. Provide this or {@link loadSchema}. */
  schema?: FormulaSchema;
  /** Async schema loader used when {@link schema} is not supplied. */
  loadSchema?: () => Promise<FormulaSchema>;
  /**
   * Names of the root objects available to this field (a subset of the context's universe).
   * When provided, only these are offered in autocomplete/palette. When omitted, all root
   * objects from the schema are used.
   */
  rootObjects?: string[];
  /** Optional server-side validation. When provided its diagnostics are authoritative. */
  validate?: (expression: string) => Promise<FormulaValidationResult>;
  readOnly?: boolean;
  /** Editor height (CSS value). Defaults to "120px". */
  height?: string;
  /** Show the browsable "Available fields" palette. Defaults to true. */
  showFieldPalette?: boolean;
  placeholder?: string;
}

function mapDiagnosticRange(doc: Text, line: number, column: number): { from: number; to: number } {
  if (line <= 0 || line > doc.lines) {
    return { from: 0, to: doc.length };
  }
  const docLine = doc.line(line);
  const from = Math.min(docLine.from + Math.max(column - 1, 0), docLine.to);
  const to = Math.min(from + 1, docLine.to) === from ? docLine.to : Math.min(from + 1, docLine.to);
  return { from, to: Math.max(to, from) };
}

/**
 * C# operators offered as quick-insert buttons. `insert` is the text written at the cursor and
 * `caretOffset` (when set) positions the caret inside the inserted text — e.g. between the
 * parentheses of `()`.
 */
interface FormulaOperator {
  label: string;
  insert: string;
  hint: string;
  caretOffset?: number;
}

const FORMULA_OPERATORS: FormulaOperator[] = [
  { label: "+", insert: " + ", hint: "Add" },
  { label: "−", insert: " - ", hint: "Subtract" },
  { label: "*", insert: " * ", hint: "Multiply" },
  { label: "/", insert: " / ", hint: "Divide" },
  { label: "%", insert: " % ", hint: "Modulo" },
  { label: "==", insert: " == ", hint: "Equal to" },
  { label: "!=", insert: " != ", hint: "Not equal to" },
  { label: "<", insert: " < ", hint: "Less than" },
  { label: ">", insert: " > ", hint: "Greater than" },
  { label: "<=", insert: " <= ", hint: "Less than or equal to" },
  { label: ">=", insert: " >= ", hint: "Greater than or equal to" },
  { label: "&&", insert: " && ", hint: "Logical AND" },
  { label: "||", insert: " || ", hint: "Logical OR" },
  { label: "!", insert: "!", hint: "Logical NOT" },
  { label: "??", insert: " ?? ", hint: "Null-coalescing" },
  { label: "( )", insert: "()", hint: "Parentheses", caretOffset: 1 },
];

export const FormulaEditor = (props: FormulaEditorProps) => {
  const { schema: schemaProp, loadSchema, validate, onValueChange, rootObjects } = props;

  const [schema, setSchema] = useState<FormulaSchema | undefined>(schemaProp);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  useEffect(() => {
    if (schemaProp) {
      setSchema(schemaProp);
      return;
    }
    if (!loadSchema) {
      return;
    }
    let cancelled = false;
    loadSchema()
      .then((loaded) => {
        if (!cancelled) {
          setSchema(loaded);
          setSchemaError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSchemaError("Unable to load formula fields.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [schemaProp, loadSchema]);

  // Restrict the schema's root objects to those available to this field (if specified).
  const filteredSchema = useMemo<FormulaSchema | undefined>(() => {
    if (!schema) {
      return undefined;
    }
    if (!rootObjects || rootObjects.length === 0) {
      return schema;
    }
    const allowed = new Set(rootObjects);
    return {
      ...schema,
      rootObjects: schema.rootObjects.filter((r) => allowed.has(r.name)),
    };
  }, [schema, rootObjects]);

  const lintSource = useCallback(
    async (view: EditorView): Promise<Diagnostic[]> => {
      const text = view.state.doc.toString();
      let result: Diagnostic[] = [];
      if (validate && text.trim().length > 0) {
        try {
          const validation = await validate(text);
          result = validation.diagnostics.map((d) => {
            const { from, to } = mapDiagnosticRange(view.state.doc, d.line, d.column);
            return {
              from,
              to,
              severity: d.severity === "Error" ? "error" : "warning",
              message: d.message,
            } as Diagnostic;
          });
          setDiagnostics(result);
          return result;
        } catch {
          // Fall back to structural checks when the validation call fails.
        }
      }
      if (filteredSchema) {
        result = findStructuralIssues(filteredSchema, text).map((issue) => ({
          from: issue.from,
          to: issue.to,
          severity: "error",
          message: issue.message,
        }));
      }
      setDiagnostics(result);
      return result;
    },
    [validate, filteredSchema],
  );

  const extensions = useMemo(() => {
    const list = [
      javascript(),
      EditorView.lineWrapping,
      linter(lintSource, { delay: 500 }),
      lintGutter(),
    ];
    if (filteredSchema) {
      list.push(
        autocompletion({
          override: [createFormulaCompletionSource(filteredSchema)],
          activateOnTyping: true,
        }),
      );
    }
    return list;
  }, [filteredSchema, lintSource]);

  const handleChange = useCallback(
    (val: string) => {
      onValueChange(val);
    },
    [onValueChange],
  );

  const handleInsert = useCallback((path: string) => {
    const view = editorRef.current?.view;
    if (!view) {
      return;
    }
    const { from, to } = view.state.selection.main;
    view.dispatch({
      changes: { from, to, insert: path },
      selection: { anchor: from + path.length },
    });
    view.focus();
  }, []);

  const handleOperatorInsert = useCallback((operator: FormulaOperator) => {
    const view = editorRef.current?.view;
    if (!view) {
      return;
    }
    const { from, to } = view.state.selection.main;
    const anchor = from + (operator.caretOffset ?? operator.insert.length);
    view.dispatch({
      changes: { from, to, insert: operator.insert },
      selection: { anchor },
    });
    view.focus();
  }, []);

  const handleUndo = useCallback(() => {
    const view = editorRef.current?.view;
    if (!view) {
      return;
    }
    undo(view);
    view.focus();
  }, []);

  const handleRedo = useCallback(() => {
    const view = editorRef.current?.view;
    if (!view) {
      return;
    }
    redo(view);
    view.focus();
  }, []);

  const handleClear = useCallback(() => {
    const view = editorRef.current?.view;
    if (!view) {
      return;
    }
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: "" },
      selection: { anchor: 0 },
    });
    view.focus();
  }, []);

  return (
    <div className="formula-editor">
      {!props.readOnly && (
        <div className="formula-editor__toolbar" role="toolbar" aria-label="Formula actions">
          <button
            type="button"
            className="formula-editor__tool"
            title="Undo"
            aria-label="Undo"
            onClick={handleUndo}
          >
            ↶
          </button>
          <button
            type="button"
            className="formula-editor__tool"
            title="Redo"
            aria-label="Redo"
            onClick={handleRedo}
          >
            ↷
          </button>
          <button
            type="button"
            className="formula-editor__tool"
            title="Clear"
            aria-label="Clear"
            onClick={handleClear}
          >
            ✕
          </button>
        </div>
      )}
      <CodeMirror
        ref={editorRef}
        value={props.value ?? ""}
        height={props.height ?? "120px"}
        editable={!props.readOnly}
        readOnly={props.readOnly}
        extensions={extensions}
        onChange={handleChange}
        placeholder={props.placeholder ?? "Enter a C# expression, e.g. TransactionInfo.Transaction.TypeName"}
        basicSetup={{
          lineNumbers: false,
          foldGutter: false,
          highlightActiveLine: false,
          autocompletion: false,
        }}
      />
      {!props.readOnly && (
        <div className="formula-editor__operators" role="toolbar" aria-label="Insert C# operator">
          {FORMULA_OPERATORS.map((operator) => (
            <button
              key={operator.label}
              type="button"
              className="formula-editor__operator"
              title={operator.hint}
              onClick={() => handleOperatorInsert(operator)}
            >
              {operator.label}
            </button>
          ))}
        </div>
      )}
      {diagnostics.length > 0 && (
        <ul className="formula-editor__diagnostics">
          {diagnostics.map((diagnostic, index) => (
            <li
              key={`${index}-${diagnostic.from}-${diagnostic.message}`}
              className={`formula-editor__diagnostic formula-editor__diagnostic--${diagnostic.severity}`}
            >
              <span className="formula-editor__diagnostic-icon" aria-hidden="true">
                {diagnostic.severity === "error" ? "⊘" : "⚠"}
              </span>
              <span className="formula-editor__diagnostic-message">{diagnostic.message}</span>
            </li>
          ))}
        </ul>
      )}
      {schemaError && <div className="formula-editor__error">{schemaError}</div>}
      {(props.showFieldPalette ?? true) && filteredSchema && (
        <FormulaFieldPalette schema={filteredSchema} onInsert={handleInsert} />
      )}
    </div>
  );
};
